import * as z from 'zod'
/**
 * Format number with K or M for thousands and millions correspondingly.
 * The function is the same as we have in the gw_app.
 * @param num number to format
 * @param localize function to localize the number. For example, intl.formatNumber
 * @param options options object containing threshold and toFixed
 * @param options.threshold threshold for formatting number
 * @param options.toFixed number of digits after the decimal point
 * @returns formatted number
 * @example numKtoM(20_000, intl.formatNumber, { threshold: 1000, toFixed: 1 }) => '20K'
 */

export type NumKtoMOptions = {
  threshold?: number
  toFixed?: number
}

// TODO: Replace it by intl.formatNumber?
export function numKtoM(
  num: number,
  localize: (num: number) => string,
  options: NumKtoMOptions = {},
) {
  const { threshold = 10_000, toFixed = 1 } = options
  const absolutNum = Math.abs(num)

  if (absolutNum > threshold) {
    if (absolutNum >= 1_000) {
      const divider = num >= 1_000_000 ? 1_000_000 : 1_000
      const unit = num >= 1_000_000 ? 'M' : 'K'
      num /= divider
      num = Number(num.toFixed(toFixed)) || 0
      return `${localize(num)}${unit}`
    }
  }

  return num.toFixed(toFixed)
}

type UnitDecimal = keyof typeof unitDecimals

export function numFixedUnit(val: number, unit: UnitDecimal) {
  return Number(Number(val || 0).toFixed(unitDecimals[unit] || 0))
}

const unitDecimals = {
  ante: 3,
  avg_ev_loss: 2,
  bb: 2, // big blinds
  bb_pot: 0, // % big blind by pot
  blind: 2, // blind in - custom tree settings
  blocker_rate: 0, //
  bounty: 0, // % bounty
  bubble_factor: 2, // bubble factor
  combos: 1, //
  e: 0, // e times min raise  - custom tree settings
  eq: 1, // equity
  eq_total: 2, // equity
  eqr: 1, // equity realization
  eqr_total: 2, // equity realization
  ev: 2, // estimated value
  ev_loss: 3,
  ev_pot: 1, // % ev in pot
  frequency_diff: 2,
  gto_score: 1, // gto score
  icm_payout_prize: 2,
  pot_odds: 1, // % pot odds
  rake_cap: 4, // rake cap
  rake_percent: 2, // % rake
  range: 2, // range 0.99
  range_percent: 1, // % 0.5
  risk_premium: 1, // risk premium
  strategy: 1, //  action.frequency or action.strategy
  strategy_eq: 1, // eq in sol. browser tab
  unblocker_rate: 0, //
  x: 1, // x times min raise  - custom tree settings
  dollar_ev: 2, // dollar ev
  table_equity: 2, // table equity
}
